import { adjustContentVisibility, SectionParagraph as Subtitle } from '@/components/shared/Content';
import type { CarMode } from './types';
import { useElementLerp } from '@/hooks/useElementLerp';
import { useIsClient } from '@/hooks/useIsClient';
import usePrefersReducedMotion from '@/hooks/usePrefersReducedMotion';
import { useSectionEventOnViewportEntrance } from '@/hooks/useSectionEventOnViewportEntrance';
import { useShowHeaderCtaOnScroll } from '@/hooks/useShowHeaderCtaOnScroll';
import useSmoothScroll from '@/hooks/useSmoothScroll';
import { easeInQuad, Flex, isFirefox, useResponsiveValue } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import Image from 'next/image';
import React, { useContext, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { mergeRefs } from 'react-merge-refs';
import styled, { css } from 'styled-components';
import CTA from './CTA';
import { CarPageContext } from './CarPageContext';
import { Section } from './StyledElements';
import cloudsUrl from './assets/footer/clouds.svg';
import layer1Url from './assets/footer/layer-1.svg';
import layer2Url from './assets/footer/layer-2.svg';
import layer3Url from './assets/footer/layer-3.svg';
import layer4Url from './assets/footer/layer-4.svg';
import layer5Url from './assets/footer/layer-5.svg';
import layer6Url from './assets/footer/layer-6.svg';
import cloudsFsdUrl from './assets/footer/fsd/clouds.svg';
import layer1FsdUrl from './assets/footer/fsd/layer-1.svg';
import layer2FsdUrl from './assets/footer/fsd/layer-2.svg';
import layer3FsdUrl from './assets/footer/fsd/layer-3.svg';
import layer4FsdUrl from './assets/footer/fsd/layer-4.svg';
import layer5FsdUrl from './assets/footer/fsd/layer-5.svg';
import layer6FsdUrl from './assets/footer/fsd/layer-6.svg';

interface LayerParallaxConfig {
  clouds: number;
  layer1: number;
  layer2: number;
  layer3: number;
  layer4: number;
  layer5: number;
  layer6: number;
  layer6X: number;
}

interface LayerPositionConfig {
  clouds: { translateY: number };
  layer1: { translateY: number };
  layer2: { translateY: number };
  layer3: { translateY: number };
  layer4: { translateY: number };
  layer5: { translateY: number };
  layer6: { translateY: number };
}

interface ResponsiveConfig {
  parallax: LayerParallaxConfig;
  positions: LayerPositionConfig;
}

interface ModeConfig {
  desktop: ResponsiveConfig;
  mobile: ResponsiveConfig;
  carDrivesAcross: boolean;
}

const MODE_CONFIGS: Record<CarMode, ModeConfig> = {
  default: {
    desktop: {
      parallax: {
        clouds: 300,
        layer1: 1000,
        layer2: 600,
        layer3: 400,
        layer4: 200,
        layer5: 0,
        layer6: 200,
        layer6X: -600,
      },
      positions: {
        clouds: { translateY: -100 },
        layer1: { translateY: 0 },
        layer2: { translateY: 0 },
        layer3: { translateY: 0 },
        layer4: { translateY: 0 },
        layer5: { translateY: 0 },
        layer6: { translateY: 0 },
      },
    },
    mobile: {
      parallax: {
        clouds: 300,
        layer1: 1000,
        layer2: 600,
        layer3: 400,
        layer4: 200,
        layer5: 0,
        layer6: 200,
        layer6X: -600,
      },
      positions: {
        clouds: { translateY: -100 },
        layer1: { translateY: 0 },
        layer2: { translateY: 0 },
        layer3: { translateY: 0 },
        layer4: { translateY: 0 },
        layer5: { translateY: 0 },
        layer6: { translateY: 0 },
      },
    },
    carDrivesAcross: true,
  },
  tesla: {
    desktop: {
      parallax: {
        clouds: 0,
        layer1: 100,
        layer2: 200,
        layer3: 300,
        layer4: 500,
        layer5: 600,
        layer6: 700,
        layer6X: 0,
      },
      positions: {
        clouds: { translateY: 100 },
        layer1: { translateY: 0 },
        layer2: { translateY: 0 },
        layer3: { translateY: 0 },
        layer4: { translateY: 0 },
        layer5: { translateY: 0 },
        layer6: { translateY: 0 },
      },
    },
    mobile: {
      parallax: {
        clouds: 0,
        layer1: 100,
        layer2: 100,
        layer3: 150,
        layer4: 200,
        layer5: 250,
        layer6: 300,
        layer6X: 0,
      },
      positions: {
        clouds: { translateY: 100 },
        layer1: { translateY: 0 },
        layer2: { translateY: 0 },
        layer3: { translateY: 0 },
        layer4: { translateY: 0 },
        layer5: { translateY: 0 },
        layer6: { translateY: 0 },
      },
    },
    carDrivesAcross: false,
  },
};

const transition = css`
  transition: transform 0.3s ease-out;
`;

const Container = styled(Section)`
  position: relative;
  perspective: 100px;
  transform-style: preserve-3d;
  transform: translateY(5px);
  ${adjustContentVisibility};
`;

const Clouds = styled(Image).attrs({
  width: 2044,
  height: 466,
  alt: 'Clouds',
})`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  ${transition}

  ${MEDIA_QUERIES.mobile`
        left: 0%;
        height: 260px;
    `}
`;

const Layer = styled(Image).attrs({
  width: 2670,
  height: 650,
  alt: 'Car insurance starting at $30 a month based on how you drive',
})`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  transform-origin: bottom center;
  width: 2670px;
  height: 650px;
  ${transition}

  ${MEDIA_QUERIES.mobile`
        left: 0%;
        height: 260px;
    `}
`;

const StyledCTA = styled(CTA)`
  align-self: center;
`;

const TitleContainer = styled(Flex)`
  ${transition}
`;

interface FooterProps {
  t?: number;
  ctaLabel?: string;
  ctaUrl?: string;
  belowCtaText?: string;
  mode?: CarMode;
  paddingTop?: number | number[];
  paddingBottom?: number | number[];
  excludedParams?: string[];
}

const Footer: React.FC<FooterProps> = ({
  t,
  ctaLabel = 'Check our prices',
  ctaUrl,
  belowCtaText = 'As low as $30/mo, based on your driving profile',
  mode = 'default',
  paddingTop,
  paddingBottom,
  excludedParams,
}) => {
  const { absoluteScroll } = useSmoothScroll();
  const isClient = useIsClient();
  const { showAdditionalHeaderCTA } = useShowHeaderCtaOnScroll();
  const { ref: btnRef, inView } = useInView();
  const { isEmbeddedPage, analytics } = useContext(CarPageContext);
  const prefersReducedMotion = usePrefersReducedMotion();
  const ctaTranslateY = useResponsiveValue([100, 200, 300]);
  const sceneTranslateX = useResponsiveValue([200, 100, 0]);
  const [viewed, setViewed] = React.useState(false);

  const modeConfig = MODE_CONFIGS[mode];
  const responsiveConfig = useResponsiveValue([modeConfig.mobile, modeConfig.desktop, modeConfig.desktop]);
  const { parallax, positions } = responsiveConfig;
  const { carDrivesAcross } = modeConfig;

  const [ref, newT] = useElementLerp(t ? t : absoluteScroll, {
    startPoint: 0.6,
    range: [0, 0.7],
  });

  useEffect(() => {
    if (inView) {
      setViewed(true);
    }

    if (viewed) {
      showAdditionalHeaderCTA(!inView);
    }
  }, [inView, showAdditionalHeaderCTA, viewed]);

  const reversedT = isFirefox() || prefersReducedMotion ? 0 : easeInQuad(1 - newT);
  const intersectionRef = useSectionEventOnViewportEntrance({
    sectionName: 'footer',
    page: analytics?.page,
    position: 12,
  });

  const layer6Transform = useMemo(() => {
    if (carDrivesAcross) {
      return `translateX(-50%) translateX(${sceneTranslateX + reversedT * parallax.layer6X}px) translateY(${positions.layer6.translateY + reversedT * parallax.layer6}px)`;
    }
    return `translateX(-50%) translateX(${sceneTranslateX}px) translateY(${positions.layer6.translateY + reversedT * parallax.layer6}px)`;
  }, [carDrivesAcross, sceneTranslateX, positions.layer6, reversedT, parallax.layer6, parallax.layer6X]);

  return (
    <Container
      ref={mergeRefs([ref, intersectionRef])}
      mb={0}
      pt={paddingTop ?? [45, 95, 95, 95]}
      pb={paddingBottom}
      width={1}
      height={[520, 894, 920]}
      position="relative"
      overflow="hidden"
      contain="paint"
    >
      <Clouds
        style={{
          transform: `translateX(-50%) translateX(${sceneTranslateX}px) translateY(${positions.clouds.translateY + reversedT * parallax.clouds}px)`,
        }}
        src={mode === 'tesla' ? cloudsFsdUrl.src : cloudsUrl.src}
      />
      <Layer
        style={{
          transform: `translateX(-50%) translateX(${sceneTranslateX}px) translateY(${positions.layer1.translateY + reversedT * parallax.layer1}px)`,
        }}
        src={mode === 'tesla' ? layer1FsdUrl.src : layer1Url.src}
      />
      <Layer
        style={{
          transform: `translateX(-50%) translateX(${sceneTranslateX}px) translateY(${positions.layer2.translateY + reversedT * parallax.layer2}px)`,
        }}
        src={mode === 'tesla' ? layer2FsdUrl.src : layer2Url.src}
      />
      <Layer
        style={{
          transform: `translateX(-50%) translateX(${sceneTranslateX}px) translateY(${positions.layer3.translateY + reversedT * parallax.layer3}px)`,
        }}
        src={mode === 'tesla' ? layer3FsdUrl.src : layer3Url.src}
      />
      <Layer
        style={{
          transform: `translateX(-50%) translateX(${sceneTranslateX}px) translateY(${positions.layer4.translateY + reversedT * parallax.layer4}px)`,
        }}
        src={mode === 'tesla' ? layer4FsdUrl.src : layer4Url.src}
      />
      {isClient && (
        <Layer
          style={{
            transform: `translateX(-50%) translateX(${sceneTranslateX}px) translateY(${positions.layer5.translateY + reversedT * parallax.layer5}px)`,
          }}
          src={mode === 'tesla' ? layer5FsdUrl.src : layer5Url.src}
        />
      )}
      <Layer
        style={{
          transform: layer6Transform,
        }}
        src={mode === 'tesla' ? layer6FsdUrl.src : layer6Url.src}
      />
      <TitleContainer
        flexDirection="column"
        style={{
          transform: `translateY(${newT * ctaTranslateY}px)`,
        }}
      >
        {!isEmbeddedPage && (
          <Flex gap="20px" flexDirection="column" ref={btnRef}>
            <StyledCTA aria-label={`${ctaLabel} ${belowCtaText}`} customUrl={ctaUrl} excludedParams={excludedParams} data-position="bottom">
              {ctaLabel}
            </StyledCTA>
            <Subtitle>{belowCtaText}</Subtitle>
          </Flex>
        )}
      </TitleContainer>
    </Container>
  );
};

export default React.memo(Footer);
